/**
 * The walkthrough clip on a What's New entry.
 *
 * Entries are sales-facing copy about a product change, and a short clip of the
 * change in use is read far more often than the paragraph describing it. The
 * player sits under the title so a reader can watch and skip the prose.
 *
 * Shaped after the frontend's `NotificationMedia`
 * (legal-opinion-frontend `components/notifications/NotificationMedia.tsx`) —
 * same frame, same no-autoplay decision — but deliberately not importing it:
 * that component gates itself on `hasNotificationMedia`, which folds in the
 * REACT_APP_ENABLE_RICH_NOTIFICATIONS flag. A changelog clip must not be
 * switched off by the notifications kill switch, and the two repos have no
 * build-time link anyway.
 */

import React from 'react';
import { Box } from '@mui/material';
import { resolveVideoMediaUrl } from '../utils/productMediaUrl';

interface ChangelogVideoProps {
  /** Stored media path — `/notifications/media/<uuid>.mp4`. */
  video?: string;
  /** `YYYY-MM-DD`. From this date on, the player is not rendered. */
  videoExpires?: string;
}

/**
 * Today as `YYYY-MM-DD` in the viewer's own timezone.
 *
 * Built from the local date parts rather than `toISOString()`, which converts
 * to UTC first — in SAST that shifts the date backwards for the first two hours
 * after midnight, so a clip would linger a day past its cutoff.
 */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Whether the clip's display window has closed.
 *
 * Compared as STRINGS, which is exact for `YYYY-MM-DD` and is how
 * `loadAllEntries` already sorts entry dates. Parsing to `Date` would reintroduce
 * the UTC-midnight trap that makes same-day comparisons wrong.
 *
 * Absent or malformed means "no cutoff" — the value is already validated at
 * parse time, and a typo that silently HID a good clip would be worse than one
 * that silently kept showing it.
 */
export function isVideoExpired(videoExpires?: string, now: Date = new Date()): boolean {
  if (!videoExpires) return false;
  return todayIso(now) >= videoExpires;
}

const ChangelogVideo: React.FC<ChangelogVideoProps> = ({ video, videoExpires }) => {
  // Gate on the RESOLVED value, not the raw prop: an unrecognised reference then
  // renders nothing rather than a black frame with dead controls.
  const src = resolveVideoMediaUrl(video);
  if (!src) return null;
  if (isVideoExpired(videoExpires)) return null;

  return (
    <Box
      sx={{
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
        lineHeight: 0,
        backgroundColor: '#000',
        my: 1.5,
      }}
      data-testid="changelog-video"
    >
      <video
        src={src}
        controls
        // No autoplay: several entries render at once, and a page that starts
        // playing on scroll is worse than one nobody presses play on.
        preload="metadata"
        playsInline
        style={{
          width: '100%',
          maxHeight: 360,
          display: 'block',
          objectFit: 'contain',
        }}
      />
    </Box>
  );
};

export default ChangelogVideo;
